/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import Waypoint from 'react-waypoint';

import { compose, getDisplayName, setDisplayName } from 'recompose';

import Retracked from 'js/lib/retracked';

import type { UserEmail } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1/email_address';

import { Heading, Section } from 'bundles/enterprise-legacy-learner-home/components/AutoHeading';
import type { OnProductCardClick } from 'bundles/enterprise-legacy-learner-home/types/Products';
import type { PropsFromWithSkillSetSaving } from 'bundles/program-home/components/ProgramHomeApp';
import { withSkillSetSaving } from 'bundles/program-home/components/ProgramHomeApp';
import MyCoursesContainer from 'bundles/program-home/components/enterprise-home/MyCoursesContainer';
import type {
  EnterpriseProgramFieldsFragment as EnterpriseProgram,
  ThirdPartyOrganizationFieldsFragment as ThirdPartyOrganization,
} from 'bundles/program-home/queries/__generated__/EnterpriseHomePageQuery';

type PropsFromCaller = {
  isVisible: boolean;
  lazy: boolean;
  onProductCardClick: OnProductCardClick;
  enterpriseProgram: EnterpriseProgram;
  thirdPartyOrganization: ThirdPartyOrganization;
  userEmails: UserEmail[];
  userId: number;
};

type Props = PropsFromCaller & PropsFromWithSkillSetSaving;

const noop = () => undefined;

const EnterpriseHomeMyCoursesProgramContent = ({
  isVisible,
  lazy,
  onProductCardClick,
  enterpriseProgram,
  thirdPartyOrganization,
  userEmails,
  userId,
  userSkillProfileStates,
  userSkillProfileStatesLoading,
  onSkillSetSaveToggle,
}: Props) => {
  const [isLazyPlaceholder, setIsLazyPlaceholder] = React.useState(lazy);
  const lazyLoad = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    () => setIsLazyPlaceholder(!isLazyPlaceholder),
    [isLazyPlaceholder]
  );

  const {
    id: programId,
    metadata: { name: programName, slug: programSlug },
  } = enterpriseProgram;

  const onProductCardClick0 = React.useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (product, productType, collectionTrackingId) =>
      onProductCardClick(product, productType, collectionTrackingId, programId),
    [onProductCardClick, programId]
  );

  const styles = {
    heading: css`
      border-bottom: 1px solid var(--cds-color-grey-300);
      padding-bottom: var(--cds-spacing-100);
      margin: var(--cds-spacing-600) 0;

      .CatalogSection-Margins {
        margin: 0 auto 64px;
      }
    `,
  };

  return (
    <div className="EnterpriseHomeMyCoursesProgramContent">
      {isLazyPlaceholder && isVisible ? <Waypoint onEnter={lazyLoad} /> : null}
      <Heading defaultLevel={2} css={styles.heading}>
        {programName}
      </Heading>
      <Section initialLevel={3}>
        {/* TODO(ppaskaris): Add a placeholder when my courses content is redesigned. */}
        {!isLazyPlaceholder && (
          <MyCoursesContainer
            programId={programId}
            programSlug={programSlug}
            programName={programName}
            userId={userId}
            onProductCardClick={onProductCardClick0}
            // TODO(ppaskaris): make toggleCatalogTab optional
            toggleCatalogTab={noop}
            userSkillProfileStates={userSkillProfileStates}
            userSkillProfileStatesLoading={userSkillProfileStatesLoading}
            onSkillSetSaveToggle={onSkillSetSaveToggle}
            // TODO(ppaskaris): Unify this type with ThirdPartyOrganizationsV1
            // @ts-expect-error MyCoursesContainer uses the Naptime type while this component provides a GraphQL one.
            thirdPartyOrganization={thirdPartyOrganization}
            userEmails={userEmails}
            shouldShowEulaBanner={false}
            isSummaryView={true}
          />
        )}
      </Section>
      {isLazyPlaceholder && isVisible ? <Waypoint onEnter={lazyLoad} /> : null}
    </div>
  );
};

// Trick to avoid interop error between hooks/recompose, specifically:
//   Error: Invalid hook call. Hooks can only be called inside of the body of a function component.
const EnterpriseHomeMyCoursesProgramContentHookProxy = (props: Props) => (
  <EnterpriseHomeMyCoursesProgramContent {...props} />
);

const enhancer = compose<Props, PropsFromCaller>(
  setDisplayName(getDisplayName(EnterpriseHomeMyCoursesProgramContent)),
  Retracked.createTrackedContainer<PropsFromCaller>(({ enterpriseProgram }) => ({ programId: enterpriseProgram.id })),
  withSkillSetSaving
);

export default enhancer(EnterpriseHomeMyCoursesProgramContentHookProxy);
